import { useCallback, useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import PropTypes from "prop-types";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import axios from "axios";

import {
  broilerDataByFarmUrl,
  broilerDataUrl,
  farmsForUserUrl,
  feedCostsUrl,
  removeFeedCostsUrl,
  updateFeedCostsUrl,
} from "api/endpoints";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getStoredSession } from "utils/userIdentity";

const costHeadings = [
  "Feed",
  "DOCs",
  "Energy and Utilities",
  "Veterinary",
  "Sanitiation and Litter",
  "Labour",
  "Services",
  "Transport and other",
];

const defaultCostColumns = [
  { label: "Name", width: "6.48cm", sortable: true },
  { label: "Description", width: "7.92cm", sortable: true },
];

const feedCostColumns = [
  { label: "Farm", width: "3.1cm" },
  { label: "Stage", width: "2.4cm" },
  { label: "Supplier", width: "2.7cm" },
  { label: "Feed Name", width: "3.1cm" },
  { label: "Feed Type", width: "2.7cm" },
  { label: "Weight (kg)", width: "2.2cm" },
  { label: "Cost", detail: "(excl. VAT)", width: "2.8cm" },
  { label: "Cost", detail: "(incl. VAT)", width: "2.8cm" },
  { label: "Cost/kg", width: "2.2cm" },
];

const selectFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
  },
  "& .MuiSelect-select": {
    minHeight: "1.4375em !important",
    display: "flex",
    alignItems: "center",
    py: "8.5px",
  },
};
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: "calc(40px + 0.3mm)",
  },
  "& .MuiInputBase-input": {
    py: "calc(8.5px + 0.2mm)",
  },
};

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && `${value}`.trim() !== "");

const readJsonStorage = (key) => {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn(`Unable to read ${key}`, error);
    return null;
  }
};

const readCachedCollection = (url) => {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(`OmnioCache:get:${url}?`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return Array.isArray(parsed?.data) ? parsed.data : null;
  } catch (error) {
    console.warn("Unable to read cached collection", error);
    return null;
  }
};

const writeCachedCollection = (url, data) => {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return;
  try {
    window.localStorage.setItem(`OmnioCache:get:${url}?`, JSON.stringify({ data }));
  } catch (error) {
    console.warn("Unable to write cached collection", error);
  }
};

const getEntryFarmId = (entry) =>
  pickFirst(entry?.farmId, entry?.farmID, entry?.FarmID, entry?.raw?.farmId, entry?.raw?.farmID);

const getEntryUserId = (entry) =>
  pickFirst(entry?.ID, entry?.id, entry?.userId, entry?.userID, entry?.UserID, entry?.raw?.ID);

const resolveUserId = () => {
  const session = getStoredSession();
  const sessionUserId = getEntryUserId(session);
  if (sessionUserId != null) return sessionUserId;

  const localSession = readJsonStorage("OmnioLogin");
  return getEntryUserId(localSession);
};

const resolveFarmId = () => {
  const session = getStoredSession();
  const sessionFarmId = getEntryFarmId(session);
  if (sessionFarmId != null) return sessionFarmId;

  const localSession = readJsonStorage("OmnioLogin");
  const localFarmId = getEntryFarmId(localSession);
  if (localFarmId != null) return localFarmId;

  const cachedBroilers = readJsonStorage("OmnioBroilerLandingCache");
  const cacheData = Array.isArray(cachedBroilers?.data) ? cachedBroilers.data : cachedBroilers;
  const broilers = readJsonStorage("broilers");
  const firstWithFarm = [cacheData, broilers]
    .filter(Array.isArray)
    .flat()
    .find((entry) => getEntryFarmId(entry));
  return getEntryFarmId(firstWithFarm);
};

const getFarmOptionId = (farm) =>
  pickFirst(farm?.farmId, farm?.FarmID, farm?.farmID, farm?.id, farm?.ID);

const getFarmOptionName = (farm) =>
  pickFirst(farm?.name, farm?.farmName, farm?.FarmName, farm?.description, farm?.Description);

const normalizeFarmOptions = (data) => {
  const source = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  const seen = new Set();
  return source
    .map((farm) => {
      const id = getFarmOptionId(farm);
      const name = getFarmOptionName(farm);
      if (id == null || name == null) return null;
      const value = String(id);
      if (seen.has(value)) return null;
      seen.add(value);
      return { value, label: String(name), raw: farm };
    })
    .filter(Boolean)
    .sort((left, right) => left.label.localeCompare(right.label));
};

const buildOptions = (items, { valueKey = "id", labelKey = "description" } = {}) => {
  const list = [];
  const map = {};

  items.forEach((item) => {
    const rawValue = valueKey === "feedname" ? item.feedname ?? item.id : item.id;
    if (rawValue == null) return;
    const value = String(rawValue);
    if (map[value]) return;

    let rawLabel;
    switch (labelKey) {
      case "value":
        rawLabel = item.value ?? item.description ?? item.feedname ?? item.id;
        break;
      case "feedname":
        rawLabel = item.feedname ?? item.value ?? item.description ?? item.id;
        break;
      default:
        rawLabel = item.description ?? item.value ?? item.feedname ?? item.id;
        break;
    }
    if (rawLabel == null || rawLabel === "") return;
    const label = String(rawLabel);
    list.push({ value, label });
    map[value] = label;
  });

  list.sort((left, right) => left.label.localeCompare(right.label));
  return { list, map };
};

const normalizeFeedDictionary = (dictData) => {
  const dict = Array.isArray(dictData) ? dictData : [];
  const valueById = {};
  const descriptionById = {};
  const normalizedDict = dict
    .map((item) => {
      const id = pickFirst(item?.id, item?.ID);
      const type = pickFirst(item?.type, item?.Type);
      if (id == null || type == null) return null;
      const value = pickFirst(item?.value, item?.Value);
      const description = pickFirst(item?.description, item?.Description, item?.name, item?.Name);
      const feedname = pickFirst(item?.feedname, item?.Feedname, item?.feedName, item?.FeedName);
      return {
        id: String(id),
        type: String(type),
        value: value != null ? String(value) : null,
        description: description != null ? String(description) : null,
        feedname: feedname != null ? String(feedname) : null,
      };
    })
    .filter(Boolean);

  normalizedDict.forEach((item) => {
    if (item.value) valueById[item.id] = item.value;
    if (item.description) descriptionById[item.id] = item.description;
  });

  const stageItems = normalizedDict.filter((item) => item.type === "10");
  const supplierItems = normalizedDict.filter((item) => item.type === "11");
  const feedItems = normalizedDict.filter((item) => item.type === "5");

  return {
    stage: buildOptions(stageItems, { labelKey: "value" }),
    supplier: buildOptions(supplierItems, { labelKey: "value" }),
    feedName: buildOptions(feedItems, { valueKey: "id", labelKey: "value" }),
    feedType: buildOptions(feedItems, { valueKey: "id", labelKey: "description" }),
    valueById,
    descriptionById,
  };
};

const toNumberOrNull = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatCurrency = (value) => {
  const numericValue = toNumberOrNull(value);
  return numericValue == null ? "" : numericValue.toFixed(2);
};

const formatNumber = (value) => {
  const numericValue = toNumberOrNull(value);
  return numericValue == null ? "" : numericValue.toFixed(2);
};

const toLocalDateTimeIso = (date = new Date()) => {
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 19);
};

const mapFeedCostRows = (data, fallbackFarmId = "") => {
  const source = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  return source.map((row, index) => {
    const id = pickFirst(row?.id, row?.ID) ?? index;
    const farmId = pickFirst(row?.farmId, row?.farmID, row?.FarmId, row?.FarmID) ?? fallbackFarmId;
    const farmName = pickFirst(row?.farmName, row?.FarmName, row?.name, row?.Name);
    const stage = pickFirst(row?.stage, row?.Stage);
    const supplier = pickFirst(row?.supplier, row?.Supplier);
    const feedName = pickFirst(row?.feedName, row?.FeedName, row?.feedname);
    const feedType = pickFirst(row?.feedType, row?.FeedType, row?.type, row?.Type);
    const weight = pickFirst(row?.weight, row?.Weight, row?.weightKg);
    const costExVat = pickFirst(row?.costExVat, row?.CostExVat, row?.costExclVat);
    const costIncVat = pickFirst(row?.costIncVat, row?.CostIncVat, row?.costInclVat);
    const costKg = pickFirst(row?.costKg, row?.CostKg, row?.costPerKg);

    return {
      id: String(id),
      farmId: farmId != null ? String(farmId) : "",
      farmName: farmName != null ? String(farmName) : "",
      stage: stage != null ? String(stage) : "",
      supplier: supplier != null ? String(supplier) : "",
      feedName: feedName != null ? String(feedName) : "",
      feedType: feedType != null ? String(feedType) : "",
      weightKg: formatNumber(weight),
      costExclVat: formatCurrency(costExVat),
      costInclVat: formatCurrency(costIncVat),
      costPerKg: formatCurrency(costKg),
    };
  });
};

function SectionTitle({ children }) {
  return (
    <MDBox mt={2} mb={1}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          bgcolor: "grey.200",
          px: 1,
          py: 0.5,
          fontSize: "0.95rem",
        }}
      >
        {children}
      </Typography>
    </MDBox>
  );
}

SectionTitle.propTypes = {
  children: PropTypes.node.isRequired,
};

function CostSectionTable({
  heading,
  feedRows,
  feedMaps,
  farmMaps,
  feedLoading,
  feedError,
  onAddFeed,
  onEditFeed,
  onDeleteFeed,
}) {
  const isFeed = heading === "Feed";
  const columns = isFeed ? feedCostColumns : defaultCostColumns;
  const tableWidth = isFeed ? "31.1cm" : "17.28cm";
  const actionColumnWidth = isFeed ? "2.02cm" : "2.88cm";

  return (
    <MDBox>
      <SectionTitle>{heading}</SectionTitle>
      <Paper variant="outlined" sx={{ pb: "0.3cm", borderRadius: 0 }}>
        <MDBox
          sx={{
            mt: "0.3cm",
            ml: "0.5cm",
            width: "calc(100% - 0.5cm)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            gap: "0.2cm",
            overflowX: isFeed ? "auto" : "hidden",
          }}
        >
          <TableContainer component={MDBox} sx={{ boxShadow: "none" }}>
            <Table
              size="small"
              sx={{
                tableLayout: "fixed",
                width: tableWidth,
                minWidth: tableWidth,
                borderCollapse: "collapse",
                "& th, & td": {
                  border: "1px solid",
                  borderColor: "grey.300",
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                  p: 0.75,
                  textAlign: "center",
                },
              }}
            >
              <colgroup>
                {columns.map((column) => (
                  <col
                    key={`${column.label}-${column.detail ?? ""}`}
                    style={{ width: column.width }}
                  />
                ))}
                <col style={{ width: actionColumnWidth }} />
              </colgroup>
              <TableBody>
                <TableRow sx={{ height: "1.5cm", bgcolor: "grey.100" }}>
                  {columns.map((column) => (
                    <TableCell
                      key={`${column.label}-${column.detail ?? ""}`}
                      sx={{ fontWeight: 700 }}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active
                          direction="asc"
                          hideSortIcon={false}
                          sx={{ "& .MuiTableSortLabel-icon": { opacity: 1 } }}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        <>
                          {column.label}
                          {column.detail && (
                            <MDBox component="span" display="block">
                              {column.detail}
                            </MDBox>
                          )}
                        </>
                      )}
                    </TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 700 }}>
                    <IconButton
                      size="small"
                      onClick={isFeed ? onAddFeed : undefined}
                      aria-label={`add ${heading}`}
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "grey.800",
                        "&:hover": { bgcolor: "grey.200" },
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {isFeed && feedLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 2, color: "text.secondary" }}
                    >
                      Loading feed costs...
                    </TableCell>
                  </TableRow>
                ) : isFeed && feedError ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 2, color: "error.main" }}
                    >
                      {feedError}
                    </TableCell>
                  </TableRow>
                ) : isFeed && feedRows.length > 0 ? (
                  feedRows.map((row) => (
                    <TableRow key={row.id} sx={{ "& td": { fontSize: "0.85rem" } }}>
                      <TableCell>
                        {farmMaps[row.farmId] || row.farmName || row.farmId || "-"}
                      </TableCell>
                      <TableCell>{feedMaps.stage[row.stage] || row.stage || "-"}</TableCell>
                      <TableCell>
                        {feedMaps.supplier[row.supplier] || row.supplier || "-"}
                      </TableCell>
                      <TableCell>
                        {feedMaps.feedName[row.feedName] || row.feedName || "-"}
                      </TableCell>
                      <TableCell>
                        {feedMaps.descriptionById[row.feedName] ||
                          feedMaps.feedType[row.feedType] ||
                          feedMaps.descriptionById[row.feedType] ||
                          row.feedType ||
                          "-"}
                      </TableCell>
                      <TableCell>{row.weightKg || "-"}</TableCell>
                      <TableCell>{row.costExclVat || "-"}</TableCell>
                      <TableCell>{row.costInclVat || "-"}</TableCell>
                      <TableCell>{row.costPerKg || "-"}</TableCell>
                      <TableCell>
                        <MDBox
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                            width: "100%",
                          }}
                        >
                          <EditIcon
                            onClick={() => onEditFeed?.(row)}
                            sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                          />
                          <DeleteIcon
                            onClick={() => onDeleteFeed?.(row)}
                            sx={{ fontSize: 28, color: "text.secondary", cursor: "pointer" }}
                          />
                        </MDBox>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      align="center"
                      sx={{ py: 2, color: "text.secondary" }}
                    >
                      No data
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </MDBox>
      </Paper>
    </MDBox>
  );
}

CostSectionTable.propTypes = {
  heading: PropTypes.string.isRequired,
  feedRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      farmId: PropTypes.string,
      farmName: PropTypes.string,
      stage: PropTypes.string,
      supplier: PropTypes.string,
      feedName: PropTypes.string,
      feedType: PropTypes.string,
      weightKg: PropTypes.string,
      costPerKg: PropTypes.string,
      costExclVat: PropTypes.string,
      costInclVat: PropTypes.string,
    })
  ),
  feedMaps: PropTypes.shape({
    stage: PropTypes.objectOf(PropTypes.string),
    supplier: PropTypes.objectOf(PropTypes.string),
    feedName: PropTypes.objectOf(PropTypes.string),
    feedType: PropTypes.objectOf(PropTypes.string),
    valueById: PropTypes.objectOf(PropTypes.string),
    descriptionById: PropTypes.objectOf(PropTypes.string),
  }),
  farmMaps: PropTypes.objectOf(PropTypes.string),
  feedLoading: PropTypes.bool,
  feedError: PropTypes.string,
  onAddFeed: PropTypes.func,
  onEditFeed: PropTypes.func,
  onDeleteFeed: PropTypes.func,
};

CostSectionTable.defaultProps = {
  feedRows: [],
  feedMaps: {
    stage: {},
    supplier: {},
    feedName: {},
    feedType: {},
    valueById: {},
    descriptionById: {},
  },
  farmMaps: {},
  feedLoading: false,
  feedError: null,
  onAddFeed: undefined,
  onEditFeed: undefined,
  onDeleteFeed: undefined,
};

function AdministrationCosts() {
  const [feedRows, setFeedRows] = useState([]);
  const [feedDialogOpen, setFeedDialogOpen] = useState(false);
  const [feedDialogError, setFeedDialogError] = useState(null);
  const [feedDialogSaving, setFeedDialogSaving] = useState(false);
  const [feedDialogInitial, setFeedDialogInitial] = useState(null);
  const [feedDictionaryLoading, setFeedDictionaryLoading] = useState(false);
  const [farmOptions, setFarmOptions] = useState([]);
  const [farmMaps, setFarmMaps] = useState({});
  const [farmsLoading, setFarmsLoading] = useState(false);
  const [feedOptions, setFeedOptions] = useState({
    stage: [],
    supplier: [],
    feedName: [],
    feedType: [],
  });
  const [feedMaps, setFeedMaps] = useState({
    stage: {},
    supplier: {},
    feedName: {},
    feedType: {},
    valueById: {},
    descriptionById: {},
  });
  const [feedCostsLoading, setFeedCostsLoading] = useState(false);
  const [feedCostsError, setFeedCostsError] = useState(null);

  const farmId = useMemo(() => resolveFarmId(), []);
  const userId = useMemo(() => resolveUserId(), []);
  const feedDictionaryUrl = useMemo(
    () => (farmId != null ? broilerDataByFarmUrl(farmId) : broilerDataUrl()),
    [farmId]
  );
  const feedCostsEndpoint = useMemo(() => feedCostsUrl(farmId), [farmId]);
  const updateFeedCostsEndpoint = useMemo(() => updateFeedCostsUrl(), []);
  const removeFeedCostsEndpoint = useMemo(() => removeFeedCostsUrl(), []);

  const applyFeedDictionary = useCallback((dictData) => {
    const dictSets = normalizeFeedDictionary(dictData);
    setFeedOptions({
      stage: dictSets.stage.list,
      supplier: dictSets.supplier.list,
      feedName: dictSets.feedName.list,
      feedType: dictSets.feedType.list,
    });
    setFeedMaps({
      stage: { ...dictSets.valueById, ...dictSets.stage.map },
      supplier: { ...dictSets.valueById, ...dictSets.supplier.map },
      feedName: { ...dictSets.valueById, ...dictSets.feedName.map },
      feedType: dictSets.feedType.map,
      valueById: dictSets.valueById,
      descriptionById: dictSets.descriptionById,
    });
  }, []);

  const applyFarmOptions = useCallback(
    (farmData) => {
      const normalized = normalizeFarmOptions(farmData);
      const fallbackFarmId = farmId != null ? String(farmId) : "";
      const hasFallback = fallbackFarmId
        ? normalized.some((option) => option.value === fallbackFarmId)
        : true;
      const nextOptions =
        hasFallback || !fallbackFarmId
          ? normalized
          : [{ value: fallbackFarmId, label: fallbackFarmId }, ...normalized];
      setFarmOptions(nextOptions);
      setFarmMaps(
        nextOptions.reduce((map, option) => {
          map[option.value] = option.label;
          return map;
        }, {})
      );
    },
    [farmId]
  );

  useEffect(() => {
    let didCancel = false;

    const url = userId != null ? farmsForUserUrl(userId) : null;
    const cachedRows = url ? readCachedCollection(url) : null;
    if (cachedRows) {
      applyFarmOptions(cachedRows);
    }

    if (!url) {
      applyFarmOptions([]);
      return () => {
        didCancel = true;
      };
    }

    const loadFarms = async () => {
      setFarmsLoading(true);
      try {
        const response = await axios.get(url);
        if (didCancel) return;
        writeCachedCollection(url, response.data);
        applyFarmOptions(response.data);
      } catch (error) {
        console.error("Failed to load farm dropdown options", error);
        if (!didCancel && !cachedRows) {
          applyFarmOptions([]);
        }
      } finally {
        if (!didCancel) setFarmsLoading(false);
      }
    };

    loadFarms();

    return () => {
      didCancel = true;
    };
  }, [applyFarmOptions, userId]);

  useEffect(() => {
    let didCancel = false;

    const cachedRows = readCachedCollection(feedDictionaryUrl);
    if (cachedRows) {
      applyFeedDictionary(cachedRows);
    }

    const loadFeedDictionary = async () => {
      setFeedDictionaryLoading(true);
      try {
        const response = await axios.get(feedDictionaryUrl);
        if (didCancel) return;
        writeCachedCollection(feedDictionaryUrl, response.data);
        applyFeedDictionary(response.data);
        setFeedDialogError(null);
      } catch (error) {
        console.error("Failed to load feed cost dropdowns", error);
        if (!didCancel && !cachedRows) {
          setFeedDialogError("Failed to load feed dropdown options");
        }
      } finally {
        if (!didCancel) setFeedDictionaryLoading(false);
      }
    };

    loadFeedDictionary();

    return () => {
      didCancel = true;
    };
  }, [applyFeedDictionary, feedDictionaryUrl]);

  const loadFeedCosts = useCallback(async () => {
    const cachedRows = readCachedCollection(feedCostsEndpoint);
    if (cachedRows) {
      setFeedRows(mapFeedCostRows(cachedRows, farmId));
    }

    setFeedCostsLoading(true);
    setFeedCostsError(null);
    try {
      const response = await axios.get(feedCostsEndpoint);
      writeCachedCollection(feedCostsEndpoint, response.data);
      setFeedRows(mapFeedCostRows(response.data, farmId));
    } catch (error) {
      console.error("Failed to load feed costs", error);
      if (!cachedRows) {
        setFeedRows([]);
        setFeedCostsError("Failed to load feed costs");
      }
    } finally {
      setFeedCostsLoading(false);
    }
  }, [feedCostsEndpoint, farmId]);

  useEffect(() => {
    loadFeedCosts();
  }, [loadFeedCosts]);

  const handleOpenFeedDialog = () => {
    setFeedDialogInitial(null);
    setFeedDialogError(null);
    setFeedDialogOpen(true);
  };

  const handleCloseFeedDialog = () => {
    setFeedDialogOpen(false);
    setFeedDialogInitial(null);
    setFeedDialogError(null);
  };

  const handleEditFeedCost = (row) => {
    const feedTypeValue =
      row.feedName && feedMaps.descriptionById[row.feedName] ? row.feedName : row.feedType ?? "";

    setFeedDialogInitial({
      id: row.id,
      farmId: row.farmId || (farmId != null ? String(farmId) : ""),
      stage: row.stage ?? "",
      supplier: row.supplier ?? "",
      feedName: row.feedName ?? "",
      feedType: feedTypeValue,
      weightKg: row.weightKg ?? "",
      costInclVat: row.costInclVat ?? "",
    });
    setFeedDialogError(null);
    setFeedDialogOpen(true);
  };

  const handleSaveFeedCost = async (values) => {
    const selectedFarmId = values.farmId || farmId;
    const resolvedFarmId = toNumberOrNull(selectedFarmId) ?? selectedFarmId;
    if (resolvedFarmId == null) {
      setFeedDialogError("Unable to resolve active farm");
      return;
    }

    const payload = {
      ...(values.id != null && values.id !== ""
        ? {
            id: toNumberOrNull(values.id) ?? values.id,
            recordDate: toLocalDateTimeIso(),
          }
        : {
            recordDate: toLocalDateTimeIso(),
          }),
      farmId: resolvedFarmId,
      stage: toNumberOrNull(values.stage) ?? values.stage,
      supplier: toNumberOrNull(values.supplier) ?? values.supplier,
      feedName: toNumberOrNull(values.feedName) ?? values.feedName,
      feedType:
        feedMaps.descriptionById[values.feedName] ||
        feedMaps.feedType[values.feedType] ||
        values.feedType,
      weight: toNumberOrNull(values.weightKg),
      costIncVat: toNumberOrNull(values.costInclVat),
    };

    setFeedDialogSaving(true);
    setFeedDialogError(null);
    try {
      await axios.post(updateFeedCostsEndpoint, payload, {
        headers: { "Content-Type": "application/json" },
      });
      setFeedDialogOpen(false);
      setFeedDialogInitial(null);
      await loadFeedCosts();
    } catch (error) {
      console.error("Failed to save feed cost", error);
      setFeedDialogError("Failed to save feed cost");
    } finally {
      setFeedDialogSaving(false);
    }
  };

  const handleDeleteFeedCost = async (row) => {
    const rowId = toNumberOrNull(row?.id) ?? row?.id;
    if (rowId == null || rowId === "") return;

    setFeedCostsError(null);
    try {
      await axios.post(
        removeFeedCostsEndpoint,
        { id: rowId },
        { headers: { "Content-Type": "application/json" } }
      );
      await loadFeedCosts();
    } catch (error) {
      console.error("Failed to delete feed cost", error);
      setFeedCostsError("Failed to delete feed cost");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox px={1}>
              <MDTypography variant="h4" fontWeight="medium">
                Costs
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
            <Card sx={{ height: "100%", width: "30cm", maxWidth: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  Production Costs
                </MDTypography>
                {costHeadings.map((heading) => (
                  <CostSectionTable
                    key={heading}
                    heading={heading}
                    feedRows={feedRows}
                    feedMaps={feedMaps}
                    farmMaps={farmMaps}
                    feedLoading={feedCostsLoading}
                    feedError={feedCostsError}
                    onAddFeed={handleOpenFeedDialog}
                    onEditFeed={handleEditFeedCost}
                    onDeleteFeed={handleDeleteFeedCost}
                  />
                ))}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <FeedCostDialog
        open={feedDialogOpen}
        onClose={handleCloseFeedDialog}
        onSave={handleSaveFeedCost}
        loading={feedDictionaryLoading}
        farmsLoading={farmsLoading}
        saving={feedDialogSaving}
        error={feedDialogError}
        options={{ ...feedOptions, farms: farmOptions }}
        initial={feedDialogInitial}
        defaultFarmId={farmId != null ? String(farmId) : ""}
      />
      <Footer />
    </DashboardLayout>
  );
}

function FeedCostDialog({
  open,
  onClose,
  onSave,
  loading,
  farmsLoading,
  saving,
  error,
  options,
  initial,
  defaultFarmId,
}) {
  const defaultForm = {
    id: null,
    farmId: defaultFarmId,
    stage: "",
    supplier: "",
    feedName: "",
    feedType: "",
    weightKg: "",
    costInclVat: "",
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (open) {
      setForm({ ...defaultForm, farmId: defaultFarmId, ...(initial || {}) });
    }
  }, [open, initial, defaultFarmId]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const canSave =
    !saving &&
    form.farmId &&
    form.stage &&
    form.supplier &&
    form.feedName &&
    form.feedType &&
    form.weightKg &&
    form.costInclVat;

  const handleSave = () => {
    if (!canSave) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1.5 }}>{form.id ? "Edit Feed Cost" : "Add Feed Cost"}</DialogTitle>
      <DialogContent>
        {loading && (
          <MDBox display="flex" justifyContent="center" py={2}>
            <CircularProgress size={20} />
          </MDBox>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} pt={1}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Farm"
              fullWidth
              size="medium"
              value={form.farmId}
              onChange={updateField("farmId")}
              disabled={farmsLoading}
              sx={selectFieldSx}
            >
              {options.farms.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Stage"
              fullWidth
              size="medium"
              value={form.stage}
              onChange={updateField("stage")}
              sx={selectFieldSx}
            >
              {options.stage.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Supplier"
              fullWidth
              size="medium"
              value={form.supplier}
              onChange={updateField("supplier")}
              sx={selectFieldSx}
            >
              {options.supplier.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Feed Name"
              fullWidth
              size="medium"
              value={form.feedName}
              onChange={updateField("feedName")}
              sx={selectFieldSx}
            >
              {options.feedName.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Feed Type"
              fullWidth
              size="medium"
              value={form.feedType}
              onChange={updateField("feedType")}
              sx={selectFieldSx}
            >
              {options.feedType.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Weight (kg)"
              fullWidth
              size="medium"
              type="number"
              value={form.weightKg}
              onChange={updateField("weightKg")}
              inputProps={{ min: 0, step: "0.01" }}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Cost (incl. VAT)"
              fullWidth
              size="medium"
              type="number"
              value={form.costInclVat}
              onChange={updateField("costInclVat")}
              inputProps={{ min: 0, step: "0.01" }}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="text" color="dark" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!canSave || loading || saving}
                sx={{ color: "#fff" }}
              >
                {saving ? <CircularProgress size={16} /> : form.id ? "Save" : "Add"}
              </Button>
            </MDBox>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

FeedCostDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  farmsLoading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  error: PropTypes.string,
  initial: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    farmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    supplier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    feedName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    feedType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weightKg: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    costInclVat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  options: PropTypes.shape({
    farms: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
      .isRequired,
    stage: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }))
      .isRequired,
    supplier: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
    ).isRequired,
    feedName: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
    ).isRequired,
    feedType: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
    ).isRequired,
  }).isRequired,
  defaultFarmId: PropTypes.string,
};

FeedCostDialog.defaultProps = {
  error: null,
  initial: null,
  defaultFarmId: "",
};

export default AdministrationCosts;
